import type { ReactNode } from 'react'
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form'
import { Icon } from '@/shared/ui/Icon'

/* Un seul rendu d'erreur et d'aide : ce qui rend 20 formulaires cohérents. */

interface Base {
  label: string
  help?: string
  error?: FieldError | { message?: string }
  required?: boolean
  wide?: boolean
}

function Wrap({ label, help, error, required, wide, children }: Base & { children: ReactNode }) {
  return (
    <label className="field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <span className="field__label">
        {label}
        {required && <span style={{ color: 'var(--bad)' }}> *</span>}
      </span>
      {children}
      {error?.message ? (
        <span className="small" style={{ color: 'var(--bad)' }}>
          {error.message}
        </span>
      ) : help ? (
        <span className="small muted">{help}</span>
      ) : null}
    </label>
  )
}

export function FieldText({
  field,
  placeholder,
  type = 'text',
  readOnly,
  ...base
}: Base & {
  field: UseFormRegisterReturn
  placeholder?: string
  type?: string
  /** Champ affiché mais non modifiable — le nom d'un rôle livré, par exemple. */
  readOnly?: boolean
}) {
  return (
    <Wrap {...base}>
      <input
        className="input"
        type={type}
        placeholder={placeholder}
        readOnly={readOnly}
        {...field}
      />
    </Wrap>
  )
}

/**
 * Champ décimal. Il reste un `<input type="text" inputmode="decimal">` :
 * la valeur transite en chaîne jusqu'à l'API, jamais en `number`.
 */
export function FieldDecimal({
  field,
  placeholder,
  suffix,
  ...base
}: Base & { field: UseFormRegisterReturn; placeholder?: string; suffix?: string }) {
  return (
    <Wrap {...base}>
      <div style={{ position: 'relative' }}>
        <input
          className="input num"
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          {...field}
          style={suffix ? { paddingRight: 56 } : undefined}
        />
        {suffix && (
          <span
            className="small muted num"
            style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)' }}
          >
            {suffix}
          </span>
        )}
      </div>
    </Wrap>
  )
}

export function FieldSelect({
  field,
  options,
  empty,
  ...base
}: Base & {
  field: UseFormRegisterReturn
  options: { value: string | number; label: string }[]
  empty?: string
}) {
  return (
    <Wrap {...base}>
      <select className="input" {...field}>
        {empty !== undefined && <option value="">{empty}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Wrap>
  )
}

export function FieldTextarea({
  field,
  placeholder,
  rows = 3,
  ...base
}: Base & { field: UseFormRegisterReturn; placeholder?: string; rows?: number }) {
  return (
    <Wrap {...base} wide>
      <textarea className="input" rows={rows} placeholder={placeholder} {...field} />
    </Wrap>
  )
}

export function FieldCheck({
  field,
  label,
  help,
}: {
  field: UseFormRegisterReturn
  label: string
  help?: string
}) {
  return (
    <label
      className="inline"
      style={{ gap: 10, alignItems: 'flex-start', gridColumn: '1 / -1', cursor: 'pointer' }}
    >
      <input
        type="checkbox"
        style={{ marginTop: 3, width: 17, height: 17, accentColor: 'var(--brand)' }}
        {...field}
      />
      <span>
        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</span>
        {help && (
          <span className="small muted" style={{ display: 'block', marginTop: 2 }}>
            {help}
          </span>
        )}
      </span>
    </label>
  )
}

/**
 * Capture photo. Le back exige un chemin (`*_photo_path`) mais n'expose pas
 * encore de route de téléversement : on génère une référence en attente.
 * Voir README « Points ouverts côté API ».
 */
/*
 * `FieldPhoto` vit dans son propre fichier : il dépose réellement la pièce
 * sur le serveur et ne se contente pas d'un état local.
 */
export { FieldPhoto } from './FieldPhoto'

export function GeoBadge({
  origin,
  latitude,
  longitude,
}: {
  origin: 'device' | 'station' | 'none'
  latitude: string
  longitude: string
}) {
  const ok = origin !== 'none'
  return (
    <div
      className="inline small"
      style={{ gap: 6, color: `var(--${ok ? 'good' : 'warn'})`, gridColumn: '1 / -1' }}
    >
      <Icon name="pin" />
      {origin === 'device' && `Position GPS enregistrée · ${latitude} / ${longitude}`}
      {origin === 'station' && `Position de la station · ${latitude} / ${longitude}`}
      {origin === 'none' && 'Position indisponible — la station n’a pas de coordonnées'}
    </div>
  )
}
